import { css, html } from "../helpers/webComponents";
import { InventoryRouteService } from "../services/InventoryRouteService";

/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
:host {
    display: contents;
}

/* Overlay background */
.overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.75);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}

/* Modal container matches footer aesthetic */
.modal {
    background-color: #898a8c58; /* same semi-transparent gray as footer */
    border: 1px solid #333333;
    border-radius: 10px;
    width: min(90vw, 720px);
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slideUp 0.18s ease;
    box-shadow: 0 4px 16px rgba(0,0,0,0.5);
}

@keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
}

/* Modal header and close button styled like footer buttons */
.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: #898a8c;
    border-bottom: 1px solid #333333;
    font-family: "Onesize";
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 14px;
}

.modal-close {
    all: unset;
    background-image: url("/assets/img/ui/button_gray.png");
    background-size: 100% 100%;
    background-position: center;
    background-repeat: no-repeat;
    padding: 8px 16px;
    cursor: pointer;
    text-transform: uppercase;
    color: white;
    font-family: "Onesize";
    font-size: 14px;
}

.modal-close:hover {
    background-image: url("/assets/img/ui/button_gray_hover.png");
}

/* Modal body */
.modal-body {
    padding: 16px;
    overflow-y: auto;
    flex: 1;
}

/* Grid for inventory items */
.items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
}

/* Individual item slots */
.item-slot {
    aspect-ratio: 1;
    background-image: url("/assets/img/ui/inventory_grid.png");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
}

.item-slot img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    image-rendering: pixelated;
    padding: 4px;
    box-sizing: border-box;
}

/* Item name label */
.item-slot .item-name {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    font-family: "Onesize";
    font-size: 9px;
    text-align: center;
    padding: 2px 4px;
    text-transform: uppercase;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    opacity: 0;
    transition: opacity 0.15s;
}

.item-slot:hover .item-name {
    opacity: 1;
}

/* Placeholder when no image is available */
.placeholder {
    width: 60%;
    height: 60%;
    background: #3333336e;
    border-radius: 4px;
}

/* Empty and loading states */
.empty-state, .loading-state {
    text-align: center;
    padding: 32px 16px;
    font-family: "Onesize";
    font-size: 12px;
    text-transform: uppercase;
    color: white;
    letter-spacing: 1px;
}

.loading-state {
    animation: pulse 1s ease infinite alternate;
}

@keyframes pulse {
    from { opacity: 0.4; }
    to   { opacity: 1; }
}
`;

/**
 * Inventory modal Web Component.
 * Call .open() to show the modal — it fetches GET /game/inventory and displays
 * each item's image from /assets/img/items/<alias>.png.
 * @author Julie Kraanen
 */
export class InventoryComponent extends HTMLElement {
    private readonly _inventoryRouteService: InventoryRouteService = new InventoryRouteService();

    private _isOpen: boolean = false;
    private _isLoading: boolean = false;
    private _inventoryItems: { alias: string; image: string | null }[] = [];

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
    }

    /**
     * Open the inventory modal and fetch the latest inventory from the server
     */
    public async open(): Promise<void> {
        this._isOpen = true;
        this._isLoading = true;
        this._inventoryItems = [];
        this.render();

        try {
            const response: { items: { alias: string; image: string | null }[] } = await this._inventoryRouteService.getInventory();
            this._inventoryItems = response.items;
        }
        catch {
            this._inventoryItems = [];
        }
        finally {
            this._isLoading = false;
            this.render();
        }
    }

    private close(): void {
        this._isOpen = false;
        this.render();
    }

    /**
     * Render the contents of the inventory modal into the shadow root.
     * Clears any existing content before re-rendering.
     * Only renders the modal overlay when {@link _isOpen} is `true`.
    */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        const styleEl: HTMLStyleElement = document.createElement("style");
        styleEl.textContent = styles;
        this.shadowRoot.appendChild(styleEl);

        if (this._isOpen) {
            this.shadowRoot.appendChild(this.renderModal());
        }
    }

    /**
     * Render the modal overlay element, including the header, close button,
     * and either a loading state, empty state, or the items grid.
     *
     * @returns HTML element of the modal overlay
    */
    private renderModal(): HTMLElement {
        const overlay: HTMLElement = html`
            <div class="overlay">
                <div class="modal">
                    <div class="modal-header">
                        <span>Inventory</span>
                        <button class="modal-close">Close</button>
                    </div>
                    <div class="modal-body">
                        ${this._isLoading
                            ? "<div class=\"loading-state\">Loading...</div>"
                            : this._inventoryItems.length === 0
                                ? "<div class=\"empty-state\">Your inventory is empty</div>"
                                : "<div class=\"items-grid\"></div>"
                        }
                    </div>
                </div>
            </div>
        `;

        overlay.addEventListener("click", (e: Event) => {
            if (e.target === overlay) {
                this.close();
            }
        });

        overlay.querySelector(".modal-close")
            ?.addEventListener("click", () => this.close());

        const grid: HTMLElement | null = overlay.querySelector(".items-grid");

        if (grid) {
            for (const item of this._inventoryItems) {
                grid.appendChild(this.renderItemSlot(item));
            }
        }

        return overlay;
    }

    /**
     * Render a single inventory item slot containing the item's image and name label.
     * Falls back to a placeholder when no image is available or the image fails to load.
     *
     * @param item - The inventory item to render, containing its alias and optional image path
     * @returns HTML element of the item slot
    */
    private renderItemSlot(item: { alias: string; image: string | null }): HTMLElement {
        const slot: HTMLElement = document.createElement("div");
        slot.className = "item-slot";

        if (item.image) {
            const img: HTMLImageElement = document.createElement("img");
            img.alt = item.alias;
            img.src = item.image;

            img.onerror = () => {
                img.remove();
                slot.insertBefore(this.renderPlaceholder(), slot.firstChild);
            };

            slot.appendChild(img);
        }
        else {
            slot.appendChild(this.renderPlaceholder());
        }

        const nameLabel: HTMLElement = document.createElement("div");
        nameLabel.className = "item-name";
        nameLabel.textContent = item.alias.replace(/-/g, " ");

        slot.appendChild(nameLabel);

        return slot;
    }

    /**
     * Render a placeholder element used when an item has no image or its image fails to load.
     *
     * @returns HTML element of the placeholder
    */
    private renderPlaceholder(): HTMLElement {
        const placeholder: HTMLElement = document.createElement("div");
        placeholder.className = "placeholder";

        return placeholder;
    }
}
